/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import {
  Layout,
  Card,
  Tabs,
  Button,
  Space,
  Input,
  Row,
  Col,
  Spin,
  Typography,
  Tag,
  message,
  Result,
} from "antd";
import {
  PrinterOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CopyOutlined,
  LinkOutlined,
  QrcodeOutlined,
  MobileOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import {
  BACKEND_BASE_URL,
  IMAGE_BASE_URL,
  QR_SHARING_LOGO_BASE64,
} from "../../constants/api";
import LS_SERVICE from "../../utils/localStorage";
import assets from "../../utils/ImagesPath";
import { displayMessage, getAPI } from "../../utils/common";
import { ERROR_MSG_TYPE } from "../../constants/messages";
import { WHATSAPP_NO } from "../../constants/constent";

// Public QR Sharing landing page URL (served by Laravel).
const QR_SHARING_URL = BACKEND_BASE_URL + "/qr-sharing";
// CDN QR library (same approach used by the Laravel booking-pass page).
const QR_LIB_SRC =
  "https://cdnjs.cloudflare.com/ajax/libs/qrcodejs/1.0.0/qrcode.min.js";

// Sticker template catalogue. `logo` flags whether the design shows the
// hotel logo (so the picker/UI can hint it, and layouts stay consistent).
const TEMPLATES = [
  { id: "classic", label: "Classic", logo: true },
  { id: "gold", label: "Premium Gold", logo: true },
  { id: "minimal", label: "Minimal", logo: false },
  { id: "tent", label: "Table Tent", logo: true },
  { id: "circle", label: "Circle Badge", logo: false },
  { id: "review", label: "Review Us", logo: true },
  { id: "ribbon", label: "Ribbon Header", logo: true },
  { id: "dark", label: "Dark Mode", logo: true },
  { id: "elegant", label: "Elegant Border", logo: true },
  { id: "compact", label: "Compact Label", logo: false },
  { id: "poster", label: "Poster", logo: true },
  { id: "roomcard", label: "Room Door Card", logo: true },
];

function QrGenerate() {
  const { Content } = Layout;
  const { Title, Text } = Typography;

  const settings = LS_SERVICE.get("setting") || {};
  const moduleEnabled = !!settings.qr_sharing_module_enable;

  const companyName = settings.company_name || "Our Hotel";
  const companyPhone = settings.company_phone || "";
  const companyAddress = settings.company_address || "";
  const logoSrc = settings.logo ? IMAGE_BASE_URL + settings.logo : assets.logo;

  const [activeTab, setActiveTab] = useState("live");
  const [template, setTemplate] = useState("classic");
  const [qrUrl, setQrUrl] = useState(QR_SHARING_URL);
  const [libLoading, setLibLoading] = useState(true);
  const [stickerLoading, setStickerLoading] = useState(false);
  // QR generated once as a data-URI, shown on screen via a plain <img> and
  // drawn into the downloaded sticker canvas.
  const [qrDataUrl, setQrDataUrl] = useState("");
  // Logo (used on screen + drawn into the sticker canvas). Replaced with a
  // same-origin base64 data-URI so the export canvas is never tainted.
  const [logoDataUrl, setLogoDataUrl] = useState(logoSrc);

  // Hidden offscreen node used only to let qrcodejs draw the QR canvas.
  const qrHiddenRef = useRef(null);

  useLayoutEffect(() => {
    if (!moduleEnabled) return;
    loadScript(QR_LIB_SRC)
      .then(() => setLibLoading(false))
      .catch(() => setLibLoading(false));
  }, []);

  // Fetch the logo as a same-origin base64 data-URI from the backend.
  // This guarantees it renders on screen AND can be drawn onto the export
  // canvas (the storage host may not send CORS headers). Falls back to the
  // raw URL / bundled asset if the endpoint is unavailable.
  useEffect(() => {
    if (!moduleEnabled) return;
    getAPI(
      QR_SHARING_LOGO_BASE64,
      (res) => {
        if (res && res.status === true && res.data) {
          setLogoDataUrl(res.data);
        }
      },
      () => {},
    );
  }, []);

  // (Re)generate the QR data-URI whenever the url changes and lib is ready.
  useEffect(() => {
    if (libLoading) return;
    generateQrDataUrl();
  }, [qrUrl, libLoading]);

  const generateQrDataUrl = () => {
    if (!qrHiddenRef.current || !window.QRCode) return;
    qrHiddenRef.current.innerHTML = "";
    const correct =
      (window.QRCode.CorrectLevel && window.QRCode.CorrectLevel.H) || 2;
    // eslint-disable-next-line no-new
    new window.QRCode(qrHiddenRef.current, {
      text: qrUrl || QR_SHARING_URL,
      width: 400, // render large; the <img> scales it down crisply
      height: 400,
      colorDark: "#14171F",
      colorLight: "#ffffff",
      correctLevel: correct,
    });

    // qrcodejs draws a <canvas> then also sets an <img> (from the canvas).
    // Poll briefly until either is ready, then store a PNG data-URI.
    let tries = 0;
    const grab = () => {
      if (!qrHiddenRef.current) return;
      const canvas = qrHiddenRef.current.querySelector("canvas");
      const img = qrHiddenRef.current.querySelector("img");
      if (canvas) {
        try {
          const url = canvas.toDataURL("image/png");
          if (url && url.length > 100) {
            setQrDataUrl(url);
            return;
          }
        } catch (e) {
          /* fall through */
        }
      }
      if (img && img.src && img.src.startsWith("data:")) {
        setQrDataUrl(img.src);
        return;
      }
      if (tries < 20) {
        tries += 1;
        setTimeout(grab, 50);
      }
    };
    grab();
  };

  const handlePrint = () => {
    const node = document.getElementById("qr-print-area");
    if (!node) return;
    const printWindow = window.open("", "_blank", "width=900,height=1100");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Sticker - ${companyName}</title>
          <style>
            @page { margin: 10mm; }
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; display:flex; align-items:center; justify-content:center; margin:0; padding:20px; }
          </style>
        </head>
        <body>${node.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const handleDownload = () => {
    if (!qrDataUrl) return;
    const link = document.createElement("a");
    link.download = "qr-sharing.png";
    link.href = qrDataUrl;
    link.click();
  };

  const copyUrl = (value) => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(value)
        .then(() => message.success("Link copied to clipboard"))
        .catch(() => message.info(value));
    } else {
      message.info(value);
    }
  };

  // Guarantee the logo we render into the snapshot is a same-origin
  // data-URI. Tries: (1) already inlined, (2) backend base64 endpoint,
  // (3) bundled same-origin asset converted to a data-URI. Any of these
  // keeps the export canvas clean (no cross-origin taint).
  const ensureLogoDataUri = async () => {
    if (logoDataUrl && logoDataUrl.startsWith("data:")) return logoDataUrl;

    const fromApi = await new Promise((resolve) => {
      getAPI(
        QR_SHARING_LOGO_BASE64,
        (res) =>
          resolve(res && res.status === true && res.data ? res.data : null),
        () => resolve(null),
      );
    });
    if (fromApi && fromApi.startsWith("data:")) {
      setLogoDataUrl(fromApi);
      return fromApi;
    }

    // Last resort: the bundled app logo is served same-origin, so it can
    // be drawn to a canvas and converted without tainting.
    const bundled = await sameOriginToDataUri(assets.logo);
    if (bundled) {
      setLogoDataUrl(bundled);
      return bundled;
    }
    return null;
  };

  // Download the selected sticker as a PNG.
  //
  // The sticker is composited directly with the Canvas 2D API (background,
  // text, logo image, QR image). We only ever draw same-origin data-URI
  // images + native primitives, so the canvas is never tainted (the SVG
  // <foreignObject> route taints the canvas in Chromium and cannot export).
  const handleDownloadSticker = async () => {
    if (!qrDataUrl) {
      displayMessage(ERROR_MSG_TYPE, "QR is still generating, please retry");
      return;
    }
    setStickerLoading(true);
    try {
      const safeLogo = await ensureLogoDataUri();
      const qrImg = await loadImage(qrDataUrl);
      const logoImg = safeLogo ? await loadImage(safeLogo) : null;

      const canvas = drawSticker(template, {
        companyName,
        companyPhone,
        companyAddress,
        qrImg,
        logoImg,
      });

      const link = document.createElement("a");
      link.download = `qr-sticker-${template}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      displayMessage(ERROR_MSG_TYPE, "Unable to generate sticker image");
    } finally {
      setStickerLoading(false);
    }
  };

  if (!moduleEnabled) {
    return (
      <Layout>
        <LayoutHeader />
        <Content>
          <Card title="Generate QR" bordered>
            <Card>
              <Result
                status="warning"
                icon={<QrcodeOutlined />}
                title="QR Sharing module is disabled"
                subTitle="The QR Sharing module is not enabled for your account yet. Please contact your Software Administrator to enable the QR Sharing module and gain access to QR Stickers and the QR Sharing Link."
                extra={
                  <Button
                    type="primary"
                    onClick={() => {
                      const phone = WHATSAPP_NO;
                      const message =
                        "Hi, I need help enabling the QR Sharing module for my account. Please assist me with enabling QR Stickers and the QR Sharing Link.";

                      const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

                      window.open(whatsappUrl, "_blank");
                    }}
                  >
                    Connect on WhatsApp
                  </Button>
                }
              />
            </Card>
          </Card>
        </Content>
        <LayoutFooter />
      </Layout>
    );
  }

  // Shared bits ------------------------------------------------------------
  const baseWrap = {
    width: "340px",
    background: "#fff",
    borderRadius: "18px",
    border: "1px solid #EAE5DC",
    padding: "24px",
    textAlign: "center",
    boxSizing: "border-box",
  };

  // On-screen QR preview (a static data-URI <img>). The downloaded sticker
  // is composited separately via the Canvas 2D compositor (drawSticker).
  const QrPlate = ({ style, size = 200 }) => (
    <div
      style={{
        display: "inline-block",
        padding: "12px",
        background: "#fff",
        borderRadius: "12px",
        lineHeight: 0,
        ...style,
      }}
    >
      {qrDataUrl ? (
        <img
          src={qrDataUrl}
          alt="QR code"
          width={size}
          height={size}
          style={{
            width: `${size}px`,
            height: `${size}px`,
            display: "block",
          }}
        />
      ) : (
        <div
          style={{
            width: `${size}px`,
            height: `${size}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#98a2b3",
            fontSize: 12,
          }}
        >
          Generating...
        </div>
      )}
    </div>
  );

  const Logo = ({ height = 46, style }) => (
    <img
      src={logoDataUrl}
      alt={companyName}
      style={{ height: `${height}px`, objectFit: "contain", ...style }}
    />
  );

  // ---- 12 sticker templates (>= 10 required) -----------------------------
  const renderTemplate = () => {
    switch (template) {
      // 1. Classic — logo + white
      case "classic":
        return (
          <div style={baseWrap}>
            <Logo height={48} style={{ marginBottom: 10 }} />
            <h2 style={{ fontSize: 20, margin: "4px 0", color: "#14171F" }}>
              {companyName}
            </h2>
            <p style={{ fontSize: 13, color: "#9d7328", margin: "0 0 14px" }}>
              Scan to Review &amp; Book Directly
            </p>
            <QrPlate />
            <p style={{ fontSize: 12, color: "#667085", marginTop: 12 }}>
              Point your phone camera at the code
            </p>
            {companyPhone && (
              <p style={{ fontSize: 13, fontWeight: 700, color: "#14171F" }}>
                {companyPhone}
              </p>
            )}
          </div>
        );

      // 2. Premium Gold — dark w/ logo
      case "gold":
        return (
          <div
            style={{
              ...baseWrap,
              background: "linear-gradient(160deg,#14171F 0%,#2b2f3a 100%)",
              border: "1px solid #b88d37",
            }}
          >
            <Logo
              height={40}
              style={{ marginBottom: 8, filter: "brightness(0) invert(1)" }}
            />
            <p
              style={{
                letterSpacing: 3,
                fontSize: 11,
                color: "#caa355",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              Scan Me
            </p>
            <h2 style={{ fontSize: 22, margin: "6px 0 2px", color: "#fff" }}>
              {companyName}
            </h2>
            <p style={{ fontSize: 12, color: "#eddcba", margin: "0 0 16px" }}>
              Reviews &bull; OTAs &bull; Direct Booking
            </p>
            <QrPlate style={{ borderRadius: 14, padding: 14 }} />
            <p style={{ fontSize: 12, color: "#caa355", marginTop: 14 }}>
              {companyPhone || "Book with us today"}
            </p>
          </div>
        );

      // 3. Minimal — dashed, no logo
      case "minimal":
        return (
          <div
            style={{
              ...baseWrap,
              borderStyle: "dashed",
              borderColor: "#b88d37",
            }}
          >
            <QrPlate />
            <h3
              style={{ fontSize: 16, margin: "14px 0 2px", color: "#14171F" }}
            >
              {companyName}
            </h3>
            <p style={{ fontSize: 12, color: "#667085", margin: 0 }}>
              Scan to explore reviews &amp; booking options
            </p>
          </div>
        );

      // 4. Table Tent — logo top, big CTA
      case "tent":
        return (
          <div style={{ ...baseWrap, padding: 0, overflow: "hidden" }}>
            <div
              style={{
                background: "#14171F",
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <Logo height={34} style={{ filter: "brightness(0) invert(1)" }} />
              <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
                {companyName}
              </span>
            </div>
            <div style={{ padding: 22 }}>
              <p
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: "#9d7328",
                  margin: "0 0 14px",
                }}
              >
                SCAN TO BOOK &amp; REVIEW
              </p>
              <QrPlate style={{ border: "1px solid #EAE5DC" }} />
              {companyPhone && (
                <p style={{ fontSize: 13, color: "#14171F", marginTop: 12 }}>
                  Reservations: <b>{companyPhone}</b>
                </p>
              )}
            </div>
          </div>
        );

      // 5. Circle Badge — round sticker, no logo
      case "circle":
        return (
          <div
            style={{
              width: 320,
              height: 320,
              borderRadius: "50%",
              background: "#fff",
              border: "4px solid #b88d37",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              boxSizing: "border-box",
              padding: 20,
            }}
          >
            <p
              style={{
                fontSize: 12,
                letterSpacing: 2,
                color: "#9d7328",
                textTransform: "uppercase",
                margin: "0 0 8px",
              }}
            >
              Scan Me
            </p>
            <QrPlate />
            <h3 style={{ fontSize: 15, margin: "10px 0 0", color: "#14171F" }}>
              {companyName}
            </h3>
          </div>
        );

      // 6. Review Us — review focused, logo
      case "review":
        return (
          <div style={{ ...baseWrap, borderTop: "6px solid #b88d37" }}>
            <Logo height={40} style={{ marginBottom: 8 }} />
            <div style={{ color: "#f5a623", fontSize: 22, letterSpacing: 2 }}>
              &#9733;&#9733;&#9733;&#9733;&#9733;
            </div>
            <h2 style={{ fontSize: 18, margin: "6px 0 2px", color: "#14171F" }}>
              Loved your stay?
            </h2>
            <p style={{ fontSize: 13, color: "#667085", margin: "0 0 14px" }}>
              Scan &amp; leave us a review
            </p>
            <QrPlate style={{ border: "1px solid #EAE5DC" }} />
            <p style={{ fontSize: 12, color: "#9d7328", marginTop: 12 }}>
              {companyName}
            </p>
          </div>
        );

      // 7. Ribbon Header — colored ribbon + logo
      case "ribbon":
        return (
          <div style={{ ...baseWrap, padding: 0, overflow: "hidden" }}>
            <div
              style={{
                background: "linear-gradient(90deg,#b88d37,#caa355)",
                padding: "14px",
                color: "#fff",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: 1,
              }}
            >
              SCAN &amp; CONNECT
            </div>
            <div style={{ padding: 22 }}>
              <Logo height={40} style={{ marginBottom: 10 }} />
              <QrPlate style={{ border: "1px solid #EAE5DC" }} />
              <h3
                style={{ fontSize: 16, margin: "12px 0 2px", color: "#14171F" }}
              >
                {companyName}
              </h3>
              {companyPhone && (
                <p style={{ fontSize: 12, color: "#667085", margin: 0 }}>
                  {companyPhone}
                </p>
              )}
            </div>
          </div>
        );

      // 8. Dark Mode — full dark, logo
      case "dark":
        return (
          <div
            style={{
              ...baseWrap,
              background: "#0f1115",
              border: "1px solid #333",
            }}
          >
            <Logo
              height={40}
              style={{ marginBottom: 10, filter: "brightness(0) invert(1)" }}
            />
            <h2 style={{ fontSize: 20, margin: "2px 0", color: "#fff" }}>
              {companyName}
            </h2>
            <p style={{ fontSize: 12, color: "#9aa0aa", margin: "0 0 16px" }}>
              Scan for reviews &amp; direct booking
            </p>
            <QrPlate style={{ borderRadius: 14 }} />
            {companyPhone && (
              <p style={{ fontSize: 12, color: "#caa355", marginTop: 14 }}>
                {companyPhone}
              </p>
            )}
          </div>
        );

      // 9. Elegant Border — double border, logo
      case "elegant":
        return (
          <div
            style={{
              ...baseWrap,
              border: "2px solid #14171F",
              outline: "6px solid #f6eedc",
              outlineOffset: "-12px",
              padding: 30,
            }}
          >
            <Logo height={42} style={{ marginBottom: 8 }} />
            <h2
              style={{
                fontSize: 18,
                margin: "4px 0",
                color: "#14171F",
                fontFamily: "Georgia, serif",
              }}
            >
              {companyName}
            </h2>
            <div
              style={{
                width: 40,
                height: 2,
                background: "#b88d37",
                margin: "8px auto 14px",
              }}
            />
            <QrPlate />
            <p style={{ fontSize: 12, color: "#667085", marginTop: 12 }}>
              Scan to Review &amp; Reserve
            </p>
          </div>
        );

      // 10. Compact Label — small, no logo
      case "compact":
        return (
          <div
            style={{
              ...baseWrap,
              width: 260,
              padding: 16,
              display: "flex",
              alignItems: "center",
              gap: 14,
              textAlign: "left",
            }}
          >
            <QrPlate style={{ padding: 6 }} />
            <div>
              <h3 style={{ fontSize: 14, margin: "0 0 4px", color: "#14171F" }}>
                {companyName}
              </h3>
              <p style={{ fontSize: 11, color: "#667085", margin: 0 }}>
                Scan to book &amp; review
              </p>
              {companyPhone && (
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#9d7328",
                    marginTop: 6,
                  }}
                >
                  {companyPhone}
                </p>
              )}
            </div>
          </div>
        );

      // 11. Poster — tall, logo + address
      case "poster":
        return (
          <div style={{ ...baseWrap, width: 320, padding: 28 }}>
            <Logo height={54} style={{ marginBottom: 12 }} />
            <h1 style={{ fontSize: 22, margin: "2px 0", color: "#14171F" }}>
              {companyName}
            </h1>
            <p
              style={{
                fontSize: 13,
                color: "#9d7328",
                margin: "4px 0 18px",
                fontWeight: 600,
              }}
            >
              BOOK DIRECT &bull; BEST PRICE &bull; REVIEW US
            </p>
            <QrPlate style={{ border: "1px solid #EAE5DC" }} />
            <p
              style={{
                fontSize: 13,
                color: "#14171F",
                marginTop: 16,
                fontWeight: 700,
              }}
            >
              Scan with your phone camera
            </p>
            {companyAddress && (
              <p style={{ fontSize: 11, color: "#667085", marginTop: 6 }}>
                {companyAddress}
              </p>
            )}
          </div>
        );

      // 12. Room Door Card — hospitality in-room card, logo
      case "roomcard":
        return (
          <div style={{ ...baseWrap, padding: 0, overflow: "hidden" }}>
            <div
              style={{
                background: "#f6eedc",
                padding: "16px 20px",
                textAlign: "center",
              }}
            >
              <Logo height={36} />
            </div>
            <div style={{ padding: 22 }}>
              <h3 style={{ fontSize: 16, margin: "0 0 2px", color: "#14171F" }}>
                Enjoying your stay?
              </h3>
              <p style={{ fontSize: 12, color: "#667085", margin: "0 0 14px" }}>
                Scan for services, reviews &amp; offers
              </p>
              <QrPlate style={{ border: "1px solid #EAE5DC" }} />
              <div
                style={{
                  marginTop: 14,
                  paddingTop: 12,
                  borderTop: "1px dashed #EAE5DC",
                }}
              >
                <p
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#14171F",
                    margin: 0,
                  }}
                >
                  {companyName}
                </p>
                {companyPhone && (
                  <p
                    style={{
                      fontSize: 12,
                      color: "#9d7328",
                      margin: "2px 0 0",
                    }}
                  >
                    Front Desk: {companyPhone}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const tabItems = [
    {
      key: "live",
      label: (
        <span>
          <MobileOutlined /> Live QR Sharing Screen
        </span>
      ),
      children: (
        <Row gutter={[24, 24]}>
          {/* LEFT: info + actions */}
          <Col xs={24} lg={10}>
            <Card
              bordered
              style={{ borderRadius: 12, height: "100%" }}
              bodyStyle={{ padding: 22 }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 6,
                }}
              >
                <Title level={4} style={{ margin: 0 }}>
                  Live Preview
                </Title>
                <Tag
                  icon={<CheckCircleFilled />}
                  color="success"
                  style={{ padding: "3px 10px", borderRadius: 20, margin: 0 }}
                >
                  Active
                </Tag>
              </div>
              <Text type="secondary">
                This is exactly what your guests see after scanning the QR code.
              </Text>

              {/* Shareable URL */}
              <div style={{ marginTop: 22 }}>
                <Text strong style={{ fontSize: 13 }}>
                  Shareable Link
                </Text>
                <Input
                  size="large"
                  value={QR_SHARING_URL}
                  readOnly
                  prefix={<LinkOutlined style={{ color: "#9d7328" }} />}
                  style={{ borderRadius: 8, marginTop: 8 }}
                />
                <Space style={{ marginTop: 12 }}>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyUrl(QR_SHARING_URL)}
                  >
                    Copy Link
                  </Button>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    href={QR_SHARING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in New Tab
                  </Button>
                </Space>
              </div>

              {/* Scannable QR for the page */}
              <div
                style={{
                  marginTop: 24,
                  padding: 16,
                  borderRadius: 12,
                  background: "#fafafa",
                  border: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    padding: 8,
                    border: "1px solid #EAE5DC",
                    lineHeight: 0,
                  }}
                >
                  {qrDataUrl ? (
                    <img
                      src={qrDataUrl}
                      alt="QR"
                      width={104}
                      height={104}
                      style={{
                        width: "104px",
                        height: "104px",
                        display: "block",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 104,
                        height: 104,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#98a2b3",
                        fontSize: 11,
                      }}
                    >
                      ...
                    </div>
                  )}
                </div>
                <div>
                  <Text strong style={{ display: "block", fontSize: 13 }}>
                    Scan to test
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Point your phone camera at this code to open the live guest
                    screen.
                  </Text>
                </div>
              </div>

              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: "1px dashed #EAE5DC",
                }}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  The preview updates automatically from your live content
                  (settings, gallery, facilities, rooms, rating &amp; OTA
                  items).
                </Text>
              </div>
            </Card>
          </Col>

          {/* RIGHT: device mockup */}
          <Col xs={24} lg={14}>
            <Card
              bordered
              style={{ borderRadius: 12, height: "100%" }}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{
                  padding: "12px 20px",
                  borderBottom: "1px solid #f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Text strong>
                  <MobileOutlined
                    style={{ marginRight: 8, color: "#9d7328" }}
                  />
                  Guest Screen Preview
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Mobile view
                </Text>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "28px 16px",
                  background:
                    "radial-gradient(circle at 50% 0%, #fbf8f0 0%, #f5f5f4 60%, #f0efec 100%)",
                }}
              >
                <div
                  style={{
                    width: 380,
                    maxWidth: "100%",
                    height: 760,
                    background: "#14171F",
                    borderRadius: 40,
                    padding: 12,
                    boxShadow:
                      "0 24px 60px rgba(20,23,31,0.25), 0 6px 18px rgba(20,23,31,0.15)",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 130,
                      height: 22,
                      background: "#14171F",
                      borderBottomLeftRadius: 14,
                      borderBottomRightRadius: 14,
                      zIndex: 2,
                    }}
                  />
                  <iframe
                    title="QR Sharing Live Preview"
                    src={QR_SHARING_URL}
                    style={{
                      width: "100%",
                      height: "100%",
                      border: 0,
                      borderRadius: 30,
                      background: "#fff",
                    }}
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      ),
    },
    {
      key: "print",
      label: (
        <span>
          <QrcodeOutlined /> Generate QR for Printing
        </span>
      ),
      children: (
        <Spin spinning={libLoading} tip="Loading QR generator...">
          <Row gutter={[24, 24]}>
            {/* Control panel */}
            <Col xs={24} lg={9}>
              <Card
                bordered
                style={{ borderRadius: 12 }}
                bodyStyle={{ padding: 20 }}
              >
                <Title level={5} style={{ marginTop: 0 }}>
                  <LinkOutlined style={{ color: "#9d7328", marginRight: 8 }} />
                  QR Target
                </Title>
                <Input
                  size="large"
                  value={qrUrl}
                  onChange={(e) => setQrUrl(e.target.value)}
                  placeholder={QR_SHARING_URL}
                  style={{ borderRadius: 8 }}
                />
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  style={{ padding: 0, marginTop: 6 }}
                  onClick={() => setQrUrl(QR_SHARING_URL)}
                >
                  Reset to QR Sharing URL
                </Button>

                <div
                  style={{
                    height: 1,
                    background: "#f0f0f0",
                    margin: "18px 0",
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 12,
                  }}
                >
                  <Title level={5} style={{ margin: 0 }}>
                    <QrcodeOutlined
                      style={{ color: "#9d7328", marginRight: 8 }}
                    />
                    Sticker Template
                  </Title>
                  <Tag color="gold" style={{ margin: 0 }}>
                    {TEMPLATES.length} designs
                  </Tag>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: 10,
                  }}
                >
                  {TEMPLATES.map((t) => {
                    const selected = t.id === template;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setTemplate(t.id)}
                        style={{
                          cursor: "pointer",
                          position: "relative",
                          padding: "12px 12px",
                          borderRadius: 10,
                          border: selected
                            ? "2px solid #b88d37"
                            : "1px solid #EAE5DC",
                          background: selected ? "#fbf8f0" : "#fff",
                          transition: "all 0.15s ease",
                          boxShadow: selected
                            ? "0 4px 12px rgba(184,141,55,0.18)"
                            : "none",
                        }}
                      >
                        {selected && (
                          <CheckCircleFilled
                            style={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              color: "#b88d37",
                              fontSize: 14,
                            }}
                          />
                        )}
                        <div
                          style={{
                            fontWeight: selected ? 700 : 600,
                            color: "#14171F",
                            fontSize: 13,
                          }}
                        >
                          {t.label}
                        </div>
                        <div
                          style={{
                            fontSize: 10,
                            marginTop: 2,
                            color: t.logo ? "#9d7328" : "#98a2b3",
                          }}
                        >
                          {t.logo ? "with logo" : "no logo"}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#f0f0f0",
                    margin: "18px 0",
                  }}
                />

                <Space direction="vertical" style={{ width: "100%" }} size={10}>
                  <Button
                    block
                    type="primary"
                    size="large"
                    icon={<DownloadOutlined />}
                    loading={stickerLoading}
                    onClick={handleDownloadSticker}
                  >
                    Download Sticker
                  </Button>
                  <Space style={{ width: "100%" }} size={10}>
                    <Button
                      icon={<PrinterOutlined />}
                      size="large"
                      onClick={handlePrint}
                    >
                      Print
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      size="large"
                      onClick={handleDownload}
                    >
                      QR only (PNG)
                    </Button>
                  </Space>
                </Space>
              </Card>
            </Col>

            {/* Live sticker preview */}
            <Col xs={24} lg={15}>
              <Card
                bordered
                style={{ borderRadius: 12, height: "100%" }}
                bodyStyle={{ padding: 0 }}
              >
                <div
                  style={{
                    padding: "12px 20px",
                    borderBottom: "1px solid #f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Text strong>Preview</Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {TEMPLATES.find((t) => t.id === template)?.label} template
                  </Text>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: 520,
                    padding: "36px 16px",
                    background:
                      "radial-gradient(circle at 50% 20%, #fbf8f0 0%, #f5f5f4 70%)",
                  }}
                >
                  <div id="qr-print-area">
                    <div
                      id="qr-sticker"
                      style={{
                        display: "inline-block",
                        filter: "drop-shadow(0 12px 30px rgba(20,23,31,0.14))",
                      }}
                    >
                      {renderTemplate()}
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        </Spin>
      ),
    },
  ];

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          bordered
          style={{ borderRadius: 12 }}
          title={
            <Space>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
                  QR Sharing Studio
                </div>
                <div
                  style={{ fontSize: 12, color: "#98a2b3", fontWeight: 400 }}
                >
                  Preview the guest screen &amp; generate printable QR stickers
                </div>
              </div>
            </Space>
          }
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="large"
          />
        </Card>
      </Content>
      <LayoutFooter />
      {/* Offscreen node: qrcodejs draws here, we read its data-URI. */}
      <div
        ref={qrHiddenRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: 400,
          height: 400,
          pointerEvents: "none",
          opacity: 0,
        }}
      />
    </Layout>
  );
}

// Load an external script once and resolve when ready.
const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load QR library"));
    document.body.appendChild(s);
  });

// Convert a SAME-ORIGIN image URL (e.g. bundled asset) to a data-URI.
// Returns null on any failure.
const sameOriginToDataUri = (url) =>
  new Promise((resolve) => {
    if (!url) return resolve(null);
    if (url.startsWith("data:")) return resolve(url);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

// Load an image (data-URI) and resolve with the HTMLImageElement.
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

// ---- Canvas sticker compositor -----------------------------------------
// Draws the chosen template onto a canvas at print resolution. Only native
// primitives + same-origin data-URI images are drawn, so export never taints.

const SCALE = 3; // 3x for crisp printing

const roundRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
};

// Draw an image scaled to fit inside a box, centered, preserving aspect.
// When `white` is true the logo is rendered as a solid-white silhouette,
// matching the on-screen CSS `filter: brightness(0) invert(1)` used on the
// dark templates.
const drawContain = (ctx, img, cx, boxW, boxH, top, white = false) => {
  if (!img) return 0;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const ratio = Math.min(boxW / iw, boxH / ih);
  const w = iw * ratio;
  const h = ih * ratio;
  const x = cx - w / 2;

  if (white) {
    // Render on an offscreen canvas, then keep only the white where the
    // logo has opaque pixels (source-in), giving a white silhouette.
    const off = document.createElement("canvas");
    off.width = Math.max(1, Math.ceil(w));
    off.height = Math.max(1, Math.ceil(h));
    const octx = off.getContext("2d");
    octx.drawImage(img, 0, 0, off.width, off.height);
    octx.globalCompositeOperation = "source-in";
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, off.width, off.height);
    ctx.drawImage(off, x, top, w, h);
  } else {
    ctx.drawImage(img, x, top, w, h);
  }
  return h;
};

const setFont = (ctx, weight, size, family) => {
  ctx.font = `${weight} ${size}px ${family || "Arial, Helvetica, sans-serif"}`;
};

// Compose the sticker. Returns a canvas element.
const drawSticker = (template, opts) => {
  const { companyName, companyPhone, companyAddress, qrImg, logoImg } = opts;

  // Logical (CSS-px) canvas size per template; circle is square.
  const W = template === "compact" ? 300 : template === "circle" ? 320 : 340;
  const H =
    template === "circle"
      ? 320
      : template === "compact"
        ? 150
        : template === "poster"
          ? 470
          : 430;

  const canvas = document.createElement("canvas");
  canvas.width = W * SCALE;
  canvas.height = H * SCALE;
  const ctx = canvas.getContext("2d");
  ctx.setTransform(SCALE, 0, 0, SCALE, 0, 0);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  const cx = W / 2;

  // Palette
  const GOLD = "#9d7328";
  const GOLD2 = "#b88d37";
  const DARK = "#14171F";
  const MUTED = "#667085";

  // Helper: centered text
  const text = (str, y, color, weight, size, family, spacing) => {
    if (!str) return;
    ctx.fillStyle = color;
    setFont(ctx, weight, size, family);
    if (spacing) {
      // simple letter-spacing
      const chars = String(str).split("");
      const total = chars.reduce(
        (acc, c) => acc + ctx.measureText(c).width + spacing,
        -spacing,
      );
      let x = cx - total / 2;
      ctx.textAlign = "left";
      chars.forEach((c) => {
        ctx.fillText(c, x, y);
        x += ctx.measureText(c).width + spacing;
      });
      ctx.textAlign = "center";
    } else {
      ctx.fillText(str, cx, y);
    }
  };

  const drawQr = (top, plate = 200, bg = "#fff", plateBorder = null) => {
    const plateSize = plate + 24;
    const px = cx - plateSize / 2;
    ctx.fillStyle = bg;
    roundRect(ctx, px, top, plateSize, plateSize, 12);
    ctx.fill();
    if (plateBorder) {
      ctx.strokeStyle = plateBorder;
      ctx.lineWidth = 1;
      roundRect(ctx, px, top, plateSize, plateSize, 12);
      ctx.stroke();
    }
    ctx.drawImage(qrImg, cx - plate / 2, top + 12, plate, plate);
    return top + plateSize;
  };

  // Background per template
  const fillBg = (color) => {
    ctx.fillStyle = color;
    roundRect(ctx, 0, 0, W, H, template === "circle" ? W / 2 : 18);
    ctx.fill();
  };

  switch (template) {
    case "gold": {
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#14171F");
      grad.addColorStop(1, "#2b2f3a");
      fillBg("#14171F");
      ctx.fillStyle = grad;
      roundRect(ctx, 0, 0, W, H, 18);
      ctx.fill();
      ctx.strokeStyle = GOLD2;
      ctx.lineWidth = 1;
      roundRect(ctx, 0.5, 0.5, W - 1, H - 1, 18);
      ctx.stroke();
      let y = 26;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 40, y, true) + 6;
      text("SCAN ME", y, "#caa355", "bold", 11, null, 3);
      y += 22;
      text(companyName, y, "#fff", "bold", 22);
      y += 30;
      text("Reviews • OTAs • Direct Booking", y, "#eddcba", "normal", 12);
      y += 22;
      y = drawQr(y, 200, "#fff");
      y += 14;
      text(companyPhone || "Book with us today", y, "#caa355", "normal", 12);
      break;
    }
    case "dark": {
      fillBg("#0f1115");
      ctx.strokeStyle = "#333";
      ctx.lineWidth = 1;
      roundRect(ctx, 0.5, 0.5, W - 1, H - 1, 18);
      ctx.stroke();
      let y = 28;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 40, y, true) + 8;
      text(companyName, y, "#fff", "bold", 20);
      y += 28;
      text("Scan for reviews & direct booking", y, "#9aa0aa", "normal", 12);
      y += 22;
      y = drawQr(y, 200, "#fff");
      y += 14;
      if (companyPhone) text(companyPhone, y, "#caa355", "normal", 12);
      break;
    }
    case "minimal": {
      fillBg("#fff");
      ctx.strokeStyle = GOLD2;
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1;
      roundRect(ctx, 1, 1, W - 2, H - 2, 18);
      ctx.stroke();
      ctx.setLineDash([]);
      let y = 40;
      y = drawQr(y, 200, "#fff");
      y += 16;
      text(companyName, y, DARK, "bold", 16);
      y += 22;
      text("Scan to explore reviews & booking options", y, MUTED, "normal", 12);
      break;
    }
    case "circle": {
      fillBg("#fff");
      ctx.strokeStyle = GOLD2;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(cx, H / 2, W / 2 - 4, 0, Math.PI * 2);
      ctx.stroke();
      let y = 54;
      text("SCAN ME", y, GOLD, "normal", 12, null, 2);
      y += 22;
      y = drawQr(y, 160, "#fff");
      y += 10;
      text(companyName, y, DARK, "bold", 15);
      break;
    }
    case "compact": {
      fillBg("#fff");
      ctx.strokeStyle = "#EAE5DC";
      ctx.lineWidth = 1;
      roundRect(ctx, 0.5, 0.5, W - 1, H - 1, 18);
      ctx.stroke();
      const qrSize = 110;
      ctx.fillStyle = "#fff";
      ctx.drawImage(qrImg, 18, (H - qrSize) / 2, qrSize, qrSize);
      ctx.textAlign = "left";
      const tx = 18 + qrSize + 16;
      ctx.fillStyle = DARK;
      setFont(ctx, "bold", 15, null);
      ctx.fillText(companyName, tx, H / 2 - 26);
      ctx.fillStyle = MUTED;
      setFont(ctx, "normal", 12, null);
      ctx.fillText("Scan to book & review", tx, H / 2 - 4);
      if (companyPhone) {
        ctx.fillStyle = GOLD;
        setFont(ctx, "bold", 12, null);
        ctx.fillText(companyPhone, tx, H / 2 + 16);
      }
      ctx.textAlign = "center";
      break;
    }
    case "tent": {
      fillBg("#fff");
      // header
      ctx.save();
      roundRect(ctx, 0, 0, W, H, 18);
      ctx.clip();
      ctx.fillStyle = DARK;
      ctx.fillRect(0, 0, W, 66);
      ctx.restore();
      let hy = 16;
      if (logoImg) {
        const lh = drawContain(ctx, logoImg, cx - 40, 60, 34, hy + 6, true);
        void lh;
      }
      ctx.fillStyle = "#fff";
      setFont(ctx, "bold", 16, null);
      ctx.fillText(companyName, cx + 10, 24);
      let y = 88;
      text("SCAN TO BOOK & REVIEW", y, GOLD, "bold", 15);
      y += 24;
      y = drawQr(y, 190, "#fff", "#EAE5DC");
      y += 12;
      if (companyPhone)
        text(`Reservations: ${companyPhone}`, y, DARK, "normal", 13);
      break;
    }
    case "ribbon": {
      fillBg("#fff");
      ctx.save();
      roundRect(ctx, 0, 0, W, H, 18);
      ctx.clip();
      const grad = ctx.createLinearGradient(0, 0, W, 0);
      grad.addColorStop(0, GOLD2);
      grad.addColorStop(1, "#caa355");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, 48);
      ctx.restore();
      ctx.fillStyle = "#fff";
      setFont(ctx, "bold", 16, null);
      ctx.fillText("SCAN & CONNECT", cx, 16);
      let y = 66;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 40, y) + 10;
      y = drawQr(y, 190, "#fff", "#EAE5DC");
      y += 12;
      text(companyName, y, DARK, "bold", 16);
      y += 22;
      if (companyPhone) text(companyPhone, y, MUTED, "normal", 12);
      break;
    }
    case "review": {
      fillBg("#fff");
      ctx.fillStyle = GOLD2;
      roundRect(ctx, 0, 0, W, 6, 3);
      ctx.fill();
      let y = 22;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 40, y) + 8;
      text("★★★★★", y, "#f5a623", "normal", 22);
      y += 30;
      text("Loved your stay?", y, DARK, "bold", 18);
      y += 26;
      text("Scan & leave us a review", y, MUTED, "normal", 13);
      y += 22;
      y = drawQr(y, 190, "#fff", "#EAE5DC");
      y += 12;
      text(companyName, y, GOLD, "normal", 12);
      break;
    }
    case "elegant": {
      fillBg("#fff");
      ctx.strokeStyle = DARK;
      ctx.lineWidth = 2;
      roundRect(ctx, 1, 1, W - 2, H - 2, 18);
      ctx.stroke();
      ctx.strokeStyle = "#f6eedc";
      ctx.lineWidth = 6;
      roundRect(ctx, 12, 12, W - 24, H - 24, 10);
      ctx.stroke();
      let y = 34;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 42, y) + 8;
      text(companyName, y, DARK, "bold", 18, "Georgia, serif");
      y += 26;
      ctx.fillStyle = GOLD2;
      ctx.fillRect(cx - 20, y, 40, 2);
      y += 14;
      y = drawQr(y, 190, "#fff");
      y += 12;
      text("Scan to Review & Reserve", y, MUTED, "normal", 12);
      break;
    }
    case "poster": {
      fillBg("#fff");
      ctx.strokeStyle = "#EAE5DC";
      ctx.lineWidth = 1;
      roundRect(ctx, 0.5, 0.5, W - 1, H - 1, 18);
      ctx.stroke();
      let y = 28;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 180, 54, y) + 10;
      text(companyName, y, DARK, "bold", 22);
      y += 30;
      text("BOOK DIRECT • BEST PRICE • REVIEW US", y, GOLD, "bold", 12);
      y += 22;
      y = drawQr(y, 200, "#fff", "#EAE5DC");
      y += 16;
      text("Scan with your phone camera", y, DARK, "bold", 13);
      y += 20;
      if (companyAddress) {
        wrapText(ctx, companyAddress, cx, y, W - 60, 14, MUTED, 11);
      }
      break;
    }
    case "roomcard": {
      fillBg("#fff");
      ctx.save();
      roundRect(ctx, 0, 0, W, H, 18);
      ctx.clip();
      ctx.fillStyle = "#f6eedc";
      ctx.fillRect(0, 0, W, 62);
      ctx.restore();
      let hy = 14;
      if (logoImg) drawContain(ctx, logoImg, cx, 150, 36, hy);
      let y = 78;
      text("Enjoying your stay?", y, DARK, "bold", 16);
      y += 24;
      text("Scan for services, reviews & offers", y, MUTED, "normal", 12);
      y += 22;
      y = drawQr(y, 185, "#fff", "#EAE5DC");
      y += 16;
      ctx.strokeStyle = "#EAE5DC";
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(30, y);
      ctx.lineTo(W - 30, y);
      ctx.stroke();
      ctx.setLineDash([]);
      y += 10;
      text(companyName, y, DARK, "bold", 13);
      y += 20;
      if (companyPhone)
        text(`Front Desk: ${companyPhone}`, y, GOLD, "normal", 12);
      break;
    }
    // classic (default)
    default: {
      fillBg("#fff");
      ctx.strokeStyle = "#EAE5DC";
      ctx.lineWidth = 1;
      roundRect(ctx, 0.5, 0.5, W - 1, H - 1, 18);
      ctx.stroke();
      let y = 26;
      if (logoImg) y += drawContain(ctx, logoImg, cx, 150, 48, y) + 8;
      text(companyName, y, DARK, "bold", 20);
      y += 28;
      text("Scan to Review & Book Directly", y, GOLD, "normal", 13);
      y += 22;
      y = drawQr(y, 200, "#fff");
      y += 14;
      text("Point your phone camera at the code", y, MUTED, "normal", 12);
      y += 20;
      if (companyPhone) text(companyPhone, y, DARK, "bold", 13);
      break;
    }
  }

  return canvas;
};

// Wrap long text within maxWidth, centered.
const wrapText = (ctx, str, cx, y, maxWidth, lineH, color, size) => {
  ctx.fillStyle = color;
  setFont(ctx, "normal", size, null);
  const words = String(str).split(" ");
  let line = "";
  let yy = y;
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, cx, yy);
      line = w;
      yy += lineH;
    } else {
      line = test;
    }
  });
  if (line) ctx.fillText(line, cx, yy);
};

export default QrGenerate;
